import { useNavigate } from "react-router";
import { useState } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../common/PageMeta";
import { useModal } from "../../hooks/useModal";
import Modal from "../common/Modal";
import api from "../../services/api";
import { usePermissions } from "../../hooks/usePermissions";
import { translateSubscriptionStatus, subscriptionStatusColor, podeCancelarAssinatura } from "../../utils/subscriptionStatus";

interface PaymentData {
    id?: string;
    /** id da venda para abrir no painel. null quando a cobranca foi recusada:
     *  tentativa recusada nao gera venda, entao nao ha o que abrir. */
    saleId?: string | null;
    billingId?: string;
    method?: string;
    firstDigits?: string;
    lastDigits?: string;
    /** valor que se tentou cobrar — usado quando nao houve liquido */
    grossAmount?: number;
    netAmount?: number;
    installments?: number;
    paidAt?: string;
    createdAt?: string;
    status?: string;
    /** motivo informado pelo adquirente quando a cobranca foi recusada */
    declineReason?: string | null;
}

interface SubscriptionItem {
    productImage?: string;
    productName?: string;
    price: number;
}

interface SubscriptionData {
    subscription?: {
        id?: string;
        status?: string;
        code?: string;
        createdAt?: string;
        currentCycle?: string | number;
        nextCycleDate?: string;
    };
    customer: {
        avatar?: string;
        name?: string;
        email?: string;
        document?: string | null;
        phone?: string | null;
    };
    plan?: {
        name?: string;
        frequency?: string;
        product?: { name?: string };
    };
    lastBilling?: {
        payment?: PaymentData;
    };
    items?: SubscriptionItem[];
    payments?: PaymentData[];
}

interface SubscriptionsDetailsProps {
    subscriptionData?: SubscriptionData; // Dados da assinatura recebidos da API
    isLoading?: boolean;
}

export default function SubscriptionsDetails({ subscriptionData }: SubscriptionsDetailsProps) {
    const { hasPermission } = usePermissions();
    const { isOpen, openModal, closeModal } = useModal();
    const [isCanceling, setIsCanceling] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationType, setNotificationType] = useState<"success" | "error">("success");

    // Função para mostrar notificação
    const displayNotification = (message: string, type: "success" | "error") => {
        setNotificationMessage(message);
        setNotificationType(type);
        setShowNotification(true);

        // Auto-esconder após 5 segundos
        setTimeout(() => {
            setShowNotification(false);
        }, 5000);
    };

    // Função para cancelar a assinatura
    const handleCancelSubscription = async () => {
        if (!subscriptionData?.subscription?.id) {
            displayNotification('ID da assinatura não encontrado', 'error');
            return;
        }

        setIsCanceling(true);
        try {
            const response = await api.delete(`/api/subscriptions/${subscriptionData.subscription.id}/cancel`, {
                data: {
                    cancelImmediately: true
                }
            });

            const message = response.data?.message || 'Assinatura cancelada com sucesso!';
            displayNotification(message, 'success');
            closeModal();

            // Opcional: recarregar dados ou redirecionar
            window.location.reload();
        } catch (error) {
            console.error('Erro ao cancelar assinatura:', error);
            const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao cancelar assinatura';
            displayNotification(errorMessage, 'error');
        } finally {
            setIsCanceling(false);
        }
    };

    // Formatação de valores numéricos para exibição
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    // Formatação de data
    const formatDate = (dateStr: string | undefined, showTime: boolean = false) => {
        if (!dateStr) return '--';

        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            ...(showTime && { hour: '2-digit', minute: '2-digit' })
        };

        return new Intl.DateTimeFormat('pt-BR', options).format(date);
    };

    // Funções utilitárias para formatação
    const formatDocument = (doc: string | null | undefined): string => {
        if (!doc) return '--';
        // Remove todos os caracteres não numéricos
        const numbers = doc.replace(/\D/g, '');

        // Verifica se é CPF ou CNPJ pelo número de dígitos
        if (numbers.length === 11) {
            // Formato CPF: 000.000.000-00
            return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else if (numbers.length === 14) {
            // Formato CNPJ: 00.000.000/0000-00
            return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return doc;
    };

    const formatPhone = (phone: string | null | undefined): string => {
        if (!phone) return '--';
        // Remove todos os caracteres não numéricos e o prefixo +55 se existir
        const numbers = phone.replace(/^\+55/, '').replace(/\D/g, '');

        // Verifica se tem 11 dígitos (com DDD e 9) ou 10 dígitos (com DDD sem 9)
        if (numbers.length === 11) {
            return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (numbers.length === 10) {
            return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return phone;
    };

    const getWhatsAppLink = (phone: string | null | undefined): string => {
        if (!phone) return '#';
        // Remove tudo exceto números e mantém o código do país se existir
        const numbers = phone.replace(/\D/g, '');
        // Adiciona o código do país (55) se não existir
        const fullNumber = numbers.startsWith('55') ? numbers : `55${numbers}`;
        return `https://wa.me/${fullNumber}`;
    };

    // Função para mapear frequência do plano para texto em português
    const getFrequencyText = (frequency: string | undefined) => {
        if (!frequency) return 'Período';
        switch (frequency.toLowerCase()) {
            case 'monthly':
                return 'Mensal';
            case 'quarterly':
                return 'Trimestral';
            case 'semiannual':
            case 'biannual':
                return 'Semestral';
            case 'annual':
            case 'yearly':
                return 'Anual';
            default:
                return 'Período';
        }
    };

    // Função para formatar método de pagamento
    const getPaymentMethodText = (payment: PaymentData | undefined) => {
        if (!payment) return '--';

        const method = payment.method?.toLowerCase();

        switch (method) {
            case 'card': {
                const firstDigits = payment.firstDigits || '';
                const lastDigits = payment.lastDigits || '';

                let cardBrand = 'Cartão';

                // Identifica a bandeira do cartão
                if (firstDigits.startsWith('4')) {
                    cardBrand = 'Visa';
                } else if (
                    firstDigits.startsWith('5') && parseInt(firstDigits[1]) >= 1 && parseInt(firstDigits[1]) <= 5 ||
                    (firstDigits.startsWith('2') && parseInt(firstDigits) >= 2221 && parseInt(firstDigits) <= 2720)
                ) {
                    cardBrand = 'Mastercard';
                } else if (firstDigits.startsWith('34') || firstDigits.startsWith('37')) {
                    cardBrand = 'Amex';
                } else if (
                    firstDigits.startsWith('36') ||
                    firstDigits.startsWith('38') ||
                    firstDigits.startsWith('39') ||
                    (firstDigits.startsWith('30') && parseInt(firstDigits[2]) >= 0 && parseInt(firstDigits[2]) <= 5)
                ) {
                    cardBrand = 'Diners';
                } else if (
                    firstDigits.startsWith('6011') ||
                    (firstDigits.startsWith('65')) ||
                    (parseInt(firstDigits.substring(0, 3)) >= 644 && parseInt(firstDigits.substring(0, 3)) <= 649)
                ) {
                    cardBrand = 'Discover';
                } else if (
                    ['401178', '401179', '438935', '457631', '457632', '504175', '627780', '636297', '636368'].some(prefix =>
                        firstDigits.startsWith(prefix)
                    )
                ) {
                    cardBrand = 'Elo';
                } else if (firstDigits.startsWith('606282')) {
                    cardBrand = 'Hipercard';
                }

                return lastDigits ? `${cardBrand} - ${lastDigits}` : cardBrand;
            }

            case 'pix':
                return 'PIX';

            case 'boleto':
                return 'Boleto';

            default:
                return method || '--';
        }
    };

    // Helper para status da assinatura
    const getStatusBadge = (status: string | undefined) => {
        if (!status) return null;
        // Tradução e cor vêm de src/utils/subscriptionStatus.ts (fonte única).
        // O mapa local não tinha 'past_due' — aparecia cru como "past_due" — e
        // tinha 'suspended' com color:'warning', que não existe em colorClasses:
        // se aquele status chegasse, o acesso a colorClasses['warning'].text
        // quebrava a tela.
        const text = translateSubscriptionStatus(status);
        const color = subscriptionStatusColor(status);

        const colorClasses: Record<string, { bg: string, text: string }> = {
            green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
            red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
            yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
            blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
            gray: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-400' },
        };

        return (
            <div className={`px-3 py-1 text-sm ${colorClasses[color].text} ${colorClasses[color].bg} rounded-full inline-flex items-center`}>
                <span className="mr-1">●</span> {text}
            </div>
        );
    };

    // Helper para status do pagamento
    const getPaymentStatusText = (status: string | undefined) => {
        if (!status) return null;
        const statusMap: Record<string, { text: string, color: string }> = {
            'pending': { text: 'Pendente', color: 'yellow' },
            'approved': { text: 'Pago', color: 'green' },
            'rejected': { text: 'Falha', color: 'red' },
            'declined': { text: 'Falha', color: 'red' },
            'failed': { text: 'Falha', color: 'red' },
            'refunded': { text: 'Reembolsado', color: 'gray' },
            'waiting_payment': { text: 'Aguardando', color: 'blue' },
            'cancelled': { text: 'Cancelado', color: 'red' },
            'canceled': { text: 'Cancelado', color: 'red' },
            'chargeback': { text: 'Chargeback', color: 'red' },
            'overdue': { text: 'Vencido', color: 'yellow' },
            'expired': { text: 'Expirado', color: 'yellow' },
        };

        const { text, color } = statusMap[status] || { text: status, color: 'gray' };

        const colorClasses: Record<string, { bg: string, text: string }> = {
            green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
            red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
            yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
            blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
            gray: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-400' },
        };

        return (
            <div className={`px-3 py-1 text-sm ${colorClasses[color].text} ${colorClasses[color].bg} rounded-full inline-flex items-center`}>
                <span className="mr-1">●</span> {text}
            </div>
        );
    };

    const navigate = useNavigate();

    // Se ainda não temos dados, podemos mostrar um estado vazio ou um placeholder
    if (!subscriptionData) {
        return (
            <>
                <PageMeta
                    title="Sellyo - Detalhes da Assinatura"
                    description="Detalhes da assinatura"
                />
                <PageBreadcrumb
                    pageTitle="Detalhes da Assinatura"
                    showBackButton={true}
                    onBack={() => navigate('/subscriptions')}
                />
                <div className="text-center p-8">
                    Nenhum dado disponível
                </div>
            </>
        );
    }

    return (
        <>
            <PageMeta
                title="Sellyo - Detalhes da Assinatura"
                description="Detalhes da assinatura"
            />

            <PageBreadcrumb
                pageTitle="Detalhes da Assinatura"
                showBackButton={true}
                onBack={() => navigate('/subscriptions')}
            />

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Coluna principal com dados da transação e cliente */}
                <div className="w-full lg:w-2/3 flex flex-col gap-6">
                    {/* Bloco de dados do cliente */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">CLIENTE</h3>

                        <div className="flex items-center mb-6">
                            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-xl mr-3 dark:bg-blue-900/30 dark:text-blue-400">
                                {subscriptionData.customer.avatar || subscriptionData.customer.name?.[0]?.toUpperCase() || "C"}
                            </div>
                            <div>
                                <p className="text-base font-medium text-gray-900 dark:text-white">{subscriptionData.customer.name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{subscriptionData.customer.email}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">CPF/CNPJ</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDocument(subscriptionData.customer.document)}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">TELEFONE</h3>
                                <div className="mt-1 flex items-center gap-2">
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                        {formatPhone(subscriptionData.customer.phone)}
                                    </p>
                                    {subscriptionData.customer.phone && (
                                        <a
                                            href={getWhatsAppLink(subscriptionData.customer.phone)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center"
                                            title="Abrir conversa no WhatsApp"
                                        >
                                            <img
                                                src="/images/brand/whatsapp.webp"
                                                alt="WhatsApp"
                                                className="h-5 w-5 hover:opacity-80 transition-opacity"
                                            />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Bloco de dados principais da assinatura */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <div className="mb-4 flex justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">ASSINATURA</h3>
                                <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-white">
                                    {subscriptionData.plan?.product?.name}
                                </p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">STATUS</h3>
                                <div className="mt-3">
                                    {getStatusBadge(subscriptionData.subscription?.status)}
                                </div>
                            </div>
                        </div>

                        <hr className="my-8 border-gray-300 dark:border-gray-700" />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">ID DA ASSINATURA</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{subscriptionData.subscription?.id?.toUpperCase()}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">CÓDIGO</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{subscriptionData.subscription?.code}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">DATA DE INÍCIO</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDate(subscriptionData.subscription?.createdAt, true)}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">CICLO DE COBRANÇA</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{subscriptionData.subscription?.currentCycle}</p>
                            </div>
                        </div>

                        <hr className="my-8 border-gray-300 dark:border-gray-700" />

                        <div className="space-y-4">
                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Plano</p>
                                <p className="text-sm text-gray-900 dark:text-white">{subscriptionData.plan?.name}</p>
                            </div>

                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Valor Líquido</p>
                                <p className="text-sm text-gray-900 dark:text-white">{formatCurrency(subscriptionData.lastBilling?.payment?.netAmount || 0)}</p>
                            </div>

                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Método de Pagamento</p>
                                <p className="text-sm text-gray-900 dark:text-white">{getPaymentMethodText(subscriptionData.lastBilling?.payment)}</p>
                            </div>

                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Parcelas</p>
                                <p className="text-sm text-gray-900 dark:text-white">{subscriptionData.lastBilling?.payment?.installments ?? '--'}</p>
                            </div>

                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Frequencia</p>
                                <p className="text-sm text-gray-900 dark:text-white">{getFrequencyText(subscriptionData.plan?.frequency)}</p>
                            </div>

                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Próxima cobrança</p>
                                <p className="text-sm text-gray-900 dark:text-white">{formatDate(subscriptionData.subscription?.nextCycleDate) || '--'}</p>
                            </div>
                        </div>


                        {podeCancelarAssinatura(subscriptionData.subscription?.status) && hasPermission('subscriptions.cancel') && (
                            <div>
                                <hr className="my-8 border-gray-300 dark:border-gray-700" />
                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={openModal}
                                        className="px-4 py-2 text-sm text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 hover:border-red-700 flex items-center gap-2 transition-colors"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Cancelar Assinatura
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Coluna lateral com detalhes do carrinho, pagamento e taxas */}
                <div className="w-full lg:w-1/3 flex flex-col gap-6">
                    {/* Bloco de produtos */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">PRODUTO</h3>

                        {/* Produto principal */}
                        {subscriptionData.items && subscriptionData.items.map((item, index) => (
                            <div key={index} className="flex items-center mb-4">
                                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-xl mr-3 dark:bg-blue-900/30 dark:text-blue-400">
                                    {item.productImage ? (
                                        <img
                                            src={item.productImage}
                                            alt={item.productName}
                                            className="w-10 h-10 object-cover rounded"
                                            onError={(e) => {
                                                // Fallback para texto em caso de erro na imagem
                                                e.currentTarget.style.display = 'none';
                                                e.currentTarget.parentElement!.textContent = item.productName?.[0]?.toUpperCase() || "P";
                                            }}
                                        />
                                    ) : (
                                        // Fallback para a primeira letra se não houver imagem
                                        item.productName?.[0]?.toUpperCase() || "P"
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.productName}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {formatCurrency(item.price)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Bloco de pagamentos */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">PAGAMENTOS</h3>

                        {subscriptionData.payments && subscriptionData.payments.length > 0 ? (
                            <div className="overflow-hidden rounded-xl bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
                                <div className="max-w-full overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="border-b bg-gray-100 dark:bg-white/[0.03] border-white/[0.05]">
                                            <tr>
                                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Data</th>
                                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Valor Líquido</th>
                                                <th className="p-4 text-right text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                            {subscriptionData.payments.map((payment, index) => {
                                                // Tentativa recusada nao gera venda: o backend manda saleId = null.
                                                // Antes a linha linkava para /sales/<billingId> e dava "Erro ao
                                                // carregar dados da venda", porque aquela venda nao existe.
                                                const saleId = payment?.saleId;
                                                const abrivel = Boolean(saleId);

                                                // netAmount vem do backend na MESMA base da linha paga: o que o
                                                // seller receberia. grossAmount fica so de reserva — e o valor da
                                                // fatura do gateway, com juros de parcelamento, outra grandeza.
                                                // Misturar os dois fazia o preco parecer ter subido 23%.
                                                const valor = payment?.netAmount || payment?.grossAmount || 0;
                                                const motivo = payment?.declineReason;

                                                return (
                                                    <tr
                                                        key={payment.id || index}
                                                        className={`transition-colors ${abrivel ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''}`}
                                                        onClick={() => { if (saleId) navigate(`/sales/${saleId}`); }}
                                                    >
                                                        <td className="p-4 text-sm text-gray-900 dark:text-white">
                                                            {formatDate(payment?.paidAt || payment?.createdAt) || '--'}
                                                            {motivo && (
                                                                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                    {motivo}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="p-4 text-sm text-gray-900 dark:text-white">
                                                            {formatCurrency(valor)}
                                                        </td>
                                                        <td className="p-4 text-sm text-gray-900 dark:text-white text-right">
                                                            {getPaymentStatusText(payment?.status)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum pagamento registrado</p>
                        )}
                    </div>

                </div>
            </div>

            {/* Componente de notificação */}
            {showNotification && (
                <div className={`fixed top-4 right-4 z-50 rounded-md p-4 shadow-md transition-all duration-300 ${notificationType === "success"
                    ? "bg-success-500 dark:bg-success-400 text-white"
                    : "bg-danger-500 dark:bg-danger-400 text-white"
                    }`}>
                    <div className="flex items-center">
                        {notificationType === "success" ? (
                            <svg
                                className="h-5 w-5 mr-2 text-white dark:text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="h-5 w-5 mr-2 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        )}
                        <span>{notificationMessage}</span>
                    </div>
                </div>
            )}

            {/* Modal de confirmação de cancelamento */}
            <Modal
                isOpen={isOpen}
                onClose={closeModal}
                title="Cancelar assinatura"
                confirmText="Confirmar cancelamento"
                cancelText="Voltar"
                onConfirm={handleCancelSubscription}
                confirmLoading={isCanceling}
                confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
            >
                <div className="py-4">
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                        Tem certeza que deseja cancelar esta assinatura?
                    </p>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex items-start">
                            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div>
                                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                    Atenção, esta ação não pode ser desfeita!
                                </h4>
                                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                    Ao clicar em confirmar, a assinatura será cancelada imediatamente e o cliente não receberá mais nenhuma cobrança.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}